import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import cn from 'classnames';
import { LinkBreak2Icon, ChevronDownIcon } from '@radix-ui/react-icons';
import {CustomTooltip} from '@src/components/tooltip/Tooltip';
import { controlVariantClasses, controlChevronClasses } from '@src/components/node-info/behavior-rules/styles';

export interface SelectProps {
  options?: { value: string; label: string }[],
  placeholder?: string,
  selectLabel?: string,
  value: string,
  isValid?: boolean,
  disabled?: boolean,
  className?: string,
  icon?: React.ReactNode
  /**
   * LF360-1823: opt-in pill skin. Absent = unchanged default trigger.
   *
   * LF360-1933 round 2: this was briefly a union with `'pill-segment'`, a
   * shell-less variant so several values could share one pill split by a
   * hairline. The design settled on separate pills instead, which left that
   * variant with no caller - and a shell-less trigger reads as a broken control
   * rather than a restyled one, so leaving it available was an invitation. One
   * value stays in the union, which keeps exactly one gate holding all of this
   * off IfStatementNodeInfo, DynamicItemsSelector and every config-driven
   * GenericNodeInfo field.
   */
  variant?: 'pill',
  onValueChange?: (value: string, extraOpts?: Record<string, unknown>) => void,
  open?: boolean,
  onOpenChange?: (open: boolean) => void,
}
export function Selectbox({
  options,
  placeholder='',
  selectLabel,
  value,
  isValid = true,
  disabled = false,
  icon,
  className,
  variant,
  onValueChange,
  open,
  onOpenChange,
}: SelectProps) {
  const selectedOption = options?.find((option) => option.value === value);
  const isValidOption =
    (options !== undefined && options?.length === 0)
    || !!options?.find((option) => option.value === value);

  return (
    <Select value={value} onValueChange={onValueChange} open={open} onOpenChange={onOpenChange}>
      <CustomTooltip content={isValidOption ? (selectedOption?.label ?? value) : 'The associated object was deleted'}>
        <SelectTrigger
          disabled={disabled}
          className={cn([{'border-destructive': !isValid}, controlVariantClasses(variant), className])}
          icon={icon ?? (variant === 'pill' ? <ChevronDownIcon className={controlChevronClasses()} /> : undefined)}
        >
          <SelectValue placeholder={placeholder} className="truncate w-full">
            <div className={cn("truncate w-full relative", { 'pl-5': !isValidOption })}>
              {!isValidOption && <LinkBreak2Icon className="left-0 top-1 absolute text-red-500"/>}
              {selectedOption?.label ?? value}
            </div>
          </SelectValue>
        </SelectTrigger>
      </CustomTooltip>
      <SelectContent>
        <SelectGroup>
          {selectLabel && <SelectLabel>Fruits</SelectLabel>}
          {options?.map((option) => (
            <SelectItem key={option.value} value={option.value} className="text-sm">{option.label}</SelectItem>
          ))}
          {options?.length === 0 && (
            <SelectItem
              key="No options"
              value="no-item"
              className="text-sm"
              disabled
            >
              No options available
            </SelectItem>
          )}
        </SelectGroup>
      </SelectContent>
    </Select>
  )
}
